/**
 * Página principal de Auditoría de Ventas Home
 */

import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Collapse,
  Tabs,
  Tab,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Search,
  FilterList,
  Clear,
  Refresh,
  Security,
  AccessTime,
  Person,
  Category,
  TouchApp,
  Badge,
  Language,
  CheckCircle,
  Timeline,
  ListAlt,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/es';

import {
  useFetchAuditoriaVentasHome,
  useFetchModulos,
  useFetchAcciones,
  useFetchUsuariosAuditoria,
  formatFechaAuditoria,
  getAccionColor,
  getModuloColor,
} from '@/shared/actions/erp/auditoria-ventas-home.actions';
import { AuditoriaFiltros, AuditoriaVentasHome } from '@/shared/interfaces/erp/auditoria-ventas-home.interface';
import { AuditoriaVentasHomeDetalle } from './components/AuditoriaVentasHomeDetalle';
import { TrazabilidadTab } from './components/TrazabilidadTab';

export const AuditoriaVentasHomePage = () => {
  const theme = useTheme();

  // Estado de tabs
  const [tabValue, setTabValue] = useState(0);

  // Estado de filtros
  const [filtros, setFiltros] = useState<AuditoriaFiltros>({
    page: 1,
    page_size: 25,
  });

  // Estado para filtros temporales (antes de aplicar)
  const [filtrosTemp, setFiltrosTemp] = useState<AuditoriaFiltros>({});
  const [fechaDesde, setFechaDesde] = useState<Dayjs | null>(null);
  const [fechaHasta, setFechaHasta] = useState<Dayjs | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Estado para detalle
  const [selectedLog, setSelectedLog] = useState<string | null>(null);
  const [detalleOpen, setDetalleOpen] = useState(false);

  // Queries
  const { data, isLoading, refetch } = useFetchAuditoriaVentasHome(filtros);
  const { data: modulos } = useFetchModulos();
  const { data: acciones } = useFetchAcciones();
  const { data: usuarios } = useFetchUsuariosAuditoria();

  // Handlers
  const handleChangePage = (_: unknown, newPage: number) => {
    setFiltros({ ...filtros, page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiltros({ ...filtros, page_size: parseInt(event.target.value, 10), page: 1 });
  };

  const handleApplyFilters = () => {
    const newFiltros: AuditoriaFiltros = {
      ...filtrosTemp,
      page: 1,
      page_size: filtros.page_size,
    };

    if (fechaDesde) {
      newFiltros.fecha_desde = fechaDesde.format('YYYY-MM-DD');
    }
    if (fechaHasta) {
      newFiltros.fecha_hasta = fechaHasta.format('YYYY-MM-DD');
    }

    setFiltros(newFiltros);
  };

  const handleClearFilters = () => {
    setFiltrosTemp({});
    setFechaDesde(null);
    setFechaHasta(null);
    setFiltros({ page: 1, page_size: filtros.page_size });
  };

  const handleViewDetail = (log: AuditoriaVentasHome) => {
    setSelectedLog(log.id_bitacora);
    setDetalleOpen(true);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  Auditoría de Ventas Home
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Registro de actividades y cambios del sistema
                </Typography>
              </Box>
              {tabValue === 0 && data?.count !== undefined && (
                <Chip
                  label={`${data.count} registros`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 1, fontWeight: 600 }}
                />
              )}
            </Box>
            {tabValue === 0 && (
              <Box display="flex" gap={0.5}>
                <Tooltip title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}>
                  <IconButton
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{
                      bgcolor: showFilters ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                    }}
                  >
                    <FilterList color={showFilters ? 'primary' : 'action'} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refrescar datos">
                  <IconButton
                    onClick={() => refetch()}
                    sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) } }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 42 },
              }}
            >
              <Tab label="Registros de Auditoría" icon={<ListAlt />} iconPosition="start" />
              <Tab label="Trazabilidad" icon={<Timeline />} iconPosition="start" />
            </Tabs>
          </Box>
        </Paper>

        {/* Tab 0: Registros de Auditoría */}
        {tabValue === 0 && (
        <>
        {/* Filtros */}
        <Collapse in={showFilters}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              borderTop: `3px solid ${theme.palette.primary.main}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList fontSize="small" />
              Filtros de búsqueda
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar"
                  placeholder="Código, usuario, descripción..."
                  value={filtrosTemp.search || ''}
                  onChange={(e) => setFiltrosTemp({ ...filtrosTemp, search: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Módulo</InputLabel>
                  <Select
                    value={filtrosTemp.modulo || ''}
                    label="Módulo"
                    onChange={(e) => setFiltrosTemp({ ...filtrosTemp, modulo: e.target.value as any })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {modulos?.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Acción</InputLabel>
                  <Select
                    value={filtrosTemp.accion || ''}
                    label="Acción"
                    onChange={(e) => setFiltrosTemp({ ...filtrosTemp, accion: e.target.value as any })}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {acciones?.map((a) => (
                      <MenuItem key={a.value} value={a.value}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Usuario</InputLabel>
                  <Select
                    value={filtrosTemp.usuario_id || ''}
                    label="Usuario"
                    onChange={(e) => setFiltrosTemp({ ...filtrosTemp, usuario_id: e.target.value as any })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {usuarios?.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.nombre || u.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <DatePicker
                  label="Desde"
                  value={fechaDesde}
                  onChange={setFechaDesde}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <DatePicker
                  label="Hasta"
                  value={fechaHasta}
                  onChange={setFechaHasta}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  size="small"
                >
                  Limpiar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleApplyFilters}
                  size="small"
                  disableElevation
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* Tabla */}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    '& .MuiTableCell-head': {
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'text.secondary',
                      py: 1.5,
                    },
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AccessTime sx={{ fontSize: 16 }} /> Fecha/Hora
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Person sx={{ fontSize: 16 }} /> Usuario
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Category sx={{ fontSize: 16 }} /> Módulo
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TouchApp sx={{ fontSize: 16 }} /> Acción
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Badge sx={{ fontSize: 16 }} /> Registro
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Language sx={{ fontSize: 16 }} /> IP
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CheckCircle sx={{ fontSize: 16 }} /> Estado
                    </Box>
                  </TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={36} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Cargando registros...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : data?.results?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Security sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron registros
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.results?.map((log) => (
                    <TableRow
                      key={log.id_bitacora}
                      hover
                      onClick={() => handleViewDetail(log)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:nth-of-type(odd)': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                        {log.fecha_hora_local || formatFechaAuditoria(log.fecha_hora)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                          {log.usuario_nombre || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.modulo}
                          size="small"
                          color={getModuloColor(log.modulo)}
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.68rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.accion}
                          size="small"
                          color={getAccionColor(log.accion)}
                          sx={{ fontWeight: 600, fontSize: '0.68rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
                          {log.registro_codigo || log.registro_id?.substring(0, 8) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
                        {log.ip_address || '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.estado_http || '-'}
                          size="small"
                          color={
                            log.estado_http && log.estado_http >= 200 && log.estado_http < 300
                              ? 'success'
                              : log.estado_http && log.estado_http >= 400
                              ? 'error'
                              : 'default'
                          }
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.7rem', minWidth: 44 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetail(log);
                            }}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                              },
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.count || 0}
            page={(filtros.page || 1) - 1}
            onPageChange={handleChangePage}
            rowsPerPage={filtros.page_size || 25}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Paper>

        </>
        )}

        {/* Tab 1: Trazabilidad */}
        {tabValue === 1 && <TrazabilidadTab />}

        {/* Dialog de detalle */}
        <AuditoriaVentasHomeDetalle
          logId={selectedLog || ''}
          open={detalleOpen}
          onClose={() => {
            setDetalleOpen(false);
            setSelectedLog(null);
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AuditoriaVentasHomePage;
